import type { FC } from "react";
import React, { useEffect, useState } from "react";

import dayjs from "dayjs";

import { InfoCardSegments, Button } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { AssetsService } from "app/services/AssetsService";
import { DefaultFetchService } from "app/services/fetchService";
import { isLinesOfBusinessSegment } from "app/services/segments/LinesOfBusinessUtils";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { useSiteInfo } from "app/services/sitesService/hooks/useSiteInfo";
import { getSiteDataFromResponse } from "app/services/sitesService/sitesUtils";

import { SegmentType } from "../../../../pages/segments/analysis/utils/resolveSegmentsType";

interface ISegmentTooltipContentProps {
    domain?: string;
    segmentName?: string;
    segmentId: string;
    onClick?: () => void;
    isOrgSegment?: boolean;
    dateModified?: string;
    segmentType: SegmentType;
    height: number;
}

const INFO_CARD_WIDTH = 394;

const fetchService = DefaultFetchService.getInstance();

type MetaData = {
    title: string;
    actionButton?: string;
    CustomContentComponent?: FC<{ segmentId: string; segmentType: SegmentType }>;
    supportsRules?: boolean;
};

const SEGMET_TYPE_META_DATA: Record<SegmentType, MetaData> = {
    [SegmentType.MySegment]: {
        title: "infotip.segment.title.my.segment",
        actionButton: "infotip.segment.button.edit",
    },
    [SegmentType.AccountSegment]: {
        title: "infotip.segment.title.shared.segment",
        actionButton: "infotip.segment.button.duplicate",
    },
};

export const SegmentTooltipContent: FC<ISegmentTooltipContentProps> = (props) => {
    const { segmentName, domain, dateModified, onClick, segmentId, segmentType, height } = props;

    // Handle case where segmentType is undefined - fallback to default segment handling
    const segmentMetaData = segmentType ? SEGMET_TYPE_META_DATA[segmentType] : null;
    const {
        title: titleKey = "infotip.segment.title.segment", // Fallback title
        actionButton: actionButtonKey,
        CustomContentComponent,
        supportsRules = true,
    } = segmentMetaData || {};

    const isLobSegment = isLinesOfBusinessSegment(segmentId);

    const { data: siteInfoResponse } = useSiteInfo({ keys: domain || "" }, undefined, {
        enabled: !!domain && !isLobSegment,
    });
    const data = domain && siteInfoResponse ? getSiteDataFromResponse(siteInfoResponse, domain) : null;
    const [rules, setRules] = useState();

    useEffect(() => {
        if (!supportsRules || !segmentId) {
            return;
        }
        const abortController = new AbortController();
        const fetchDataRules = async () => {
            try {
                const response: any = await fetchService.get(
                    `/api/userdata/segments/customsegments/${segmentId}`,
                    null,
                    { cancellation: abortController.signal },
                );
                setRules(response.rules);
            } catch (e) {}
        };
        void fetchDataRules();

        return function cleanup() {
            abortController.abort();
        };
    }, [segmentId, supportsRules]);

    const editButton = React.useCallback(
        (onClick: (() => void) | undefined) => {
            return (
                onClick &&
                actionButtonKey && (
                    <Button
                        type={"primary"}
                        onClick={onClick}
                    >
                        {translate(actionButtonKey)}
                    </Button>
                )
            );
        },
        [onClick, actionButtonKey],
    );

    // Don't render tooltip content for Lines of Business segments
    if (isLobSegment) {
        return null;
    }

    if (data) {
        return (
            <InfoCardSegments
                isLoadingData={false}
                imgSrc={(data.image as string) || AssetsService.assetUrl("/images/new-no-image.png")}
                icon={data.icon || AssetsService.assetUrl("/images/autocomplete-default.png")}
                segmentName={segmentName}
                websiteName={domain}
                title={translate(titleKey)}
                rulesPrefix={translate("infotip.segment.prefix.rules")}
                dateModifiedLabel={translate("infotip.segment.date.modified.label")}
                dateModified={dateModified ? dayjs(dateModified).format("MMMM DD YYYY") : "-"}
                // @ts-ignore FIXME: strict migration - rules state defaults to undefined before fetch; getRulesString requires IRule[]
                rules={SegmentsUtils.getRulesString(rules)}
                Button={editButton(onClick)}
                width={INFO_CARD_WIDTH}
                height={height}
                CustomContent={
                    CustomContentComponent ? (
                        <CustomContentComponent
                            segmentId={segmentId}
                            segmentType={segmentType}
                        />
                    ) : null
                }
            />
        );
    }
    return <InfoCardSegments isLoadingData={true} />;
};

SegmentTooltipContent.displayName = "SegmentTooltipContent";
